import React from "react";
import { motion } from "framer-motion";
import {
  Droplet,
  Zap,
  Beaker,
  Flame,
  Leaf,
  Sparkles,
} from "lucide-react";

const SUPPLEMENTS = [
  {
    n: "01",
    name: "Dumbbell Water Bottle",
    sub: "Signature Hydration",
    cat: "HYDRATION · 32 OZ",
    Icon: Droplet,
    tint: "silver",
    line: "MBS",
    img: "https://images.unsplash.com/photo-1523362628745-0c100150b504?w=800&q=80&auto=format&fit=crop",
  },
  {
    n: "02",
    name: "Just Level Up Pre-Workout",
    sub: "Overload Charge",
    cat: "SUPPLEMENT · STIM",
    Icon: Zap,
    tint: "white",
    line: "JUST LEVEL UP",
    img: "https://images.unsplash.com/photo-1579722820308-d74e571900a9?w=800&q=80&auto=format&fit=crop",
  },
  {
    n: "03",
    name: "Just Level Up Clear Isolate Protein",
    sub: "Whey Isolate · Clear",
    cat: "PROTEIN · ISOLATE",
    Icon: Beaker,
    tint: "silver",
    line: "JUST LEVEL UP",
    img: "https://images.unsplash.com/photo-1594381898411-846e7d193883?w=800&q=80&auto=format&fit=crop",
  },
  {
    n: "04",
    name: "Just Level Up EAA & BCAA",
    sub: "Intra-Session Fuel",
    cat: "RECOVERY · AMINO",
    Icon: Flame,
    tint: "white",
    line: "JUST LEVEL UP",
    img: "https://images.unsplash.com/photo-1584464491033-06628f3a6b7b?w=800&q=80&auto=format&fit=crop",
  },
  {
    n: "05",
    name: "Just Level Up Energy Drink",
    sub: "Ready-To-Drink Charge",
    cat: "ENERGY · CAN · 16 OZ",
    Icon: Sparkles,
    tint: "silver",
    line: "JUST LEVEL UP",
    img: "https://images.unsplash.com/photo-1622483767028-3f66f32aef97?w=800&q=80&auto=format&fit=crop",
  },
  {
    n: "06",
    name: "Herbal Blends",
    sub: "Ritual Tinctures",
    cat: "RITUAL · APOTHECARY",
    Icon: Leaf,
    tint: "silver",
    line: "MBS",
    img: "https://images.unsplash.com/photo-1611071536671-cf9a2c46f19e?w=800&q=80&auto=format&fit=crop",
  },
];

const SupplementTile = ({ item, feature }) => {
  const { Icon } = item;
  return (
    <div
      className={`group relative overflow-hidden bg-black border border-white/10 ${
        feature ? "min-h-[440px] md:min-h-[520px]" : "min-h-[320px]"
      }`}
    >
      {/* Product photograph (grayscale placeholder) */}
      {item.img && (
        <img
          src={item.img}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover grayscale contrast-[1.05] opacity-70 transition-transform duration-1000 group-hover:scale-105"
        />
      )}
      {/* Dark overlay so COMING SOON reads over the photo */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Clipped corner */}
      <div
        className="absolute top-0 right-0 w-12 h-12 z-20 bg-black"
        style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }}
      />
      <div className="absolute top-2 right-2 z-30 font-mono text-[8px] tracking-[0.32em] text-silver-200">
        N° {item.n}
      </div>

      {/* Product line tag */}
      <div className="absolute top-3 left-3 z-30 font-mono text-[8px] tracking-[0.35em] text-silver-100/80">
        {item.line}
      </div>

      {/* Layered texture */}
      <div
        className="absolute inset-0 opacity-[0.16] transition-opacity duration-700 group-hover:opacity-[0.30]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(45deg, transparent 0, transparent 22px, rgba(229,228,226,0.28) 22px, rgba(229,228,226,0.28) 23px)",
        }}
      />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_35%,rgba(229,228,226,0.14)_0%,transparent_55%)] transition-transform duration-1000 group-hover:scale-110" />
      <div className="absolute inset-0 grain-overlay opacity-[0.10]" />

      {/* Icon glyph */}
      <div className="absolute inset-x-0 top-12 md:top-16 flex justify-center z-10 opacity-90">
        <div
          className={`relative flex items-center justify-center rounded-full ${
            feature ? "w-40 h-40 md:w-52 md:h-52" : "w-24 h-24"
          } border border-silver-100/30 backdrop-blur-sm bg-black/30`}
        >
          <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle,rgba(229,228,226,0.15),transparent_70%)]" />
          <Icon
            className={`${
              feature ? "h-16 w-16 md:h-20 md:w-20" : "h-9 w-9"
            } ${item.tint === "white" ? "text-white" : "text-silver-100"}`}
            strokeWidth={1.2}
          />
          <div className="absolute -inset-3 rounded-full border border-white/6 group-hover:scale-105 transition-transform duration-700" />
        </div>
      </div>

      {/* Overlay COMING SOON */}
      <div
        className={`absolute ${
          feature ? "bottom-28 md:bottom-36" : "bottom-20"
        } inset-x-0 flex flex-col items-center z-20 transition-transform duration-700 group-hover:-translate-y-1`}
      >
        <div
          className="font-serif leading-[0.85] tracking-[-0.02em] text-3xl md:text-5xl"
          style={{
            WebkitTextStroke: "1px rgba(229,228,226,0.38)",
            color: "transparent",
          }}
        >
          COMING
        </div>
        <div className="font-serif italic text-silver-100 text-3xl md:text-5xl mt-1">
          soon.
        </div>
      </div>

      {/* Info footer */}
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 z-30 border-t border-white/10 bg-black/70 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <h3
              className={`font-serif text-white ${
                feature ? "text-2xl md:text-3xl" : "text-base md:text-lg"
              } leading-tight tracking-[-0.01em] truncate`}
            >
              {item.name}
            </h3>
            <div className="font-serif italic text-silver-200 text-[13px] mt-0.5">
              {item.sub}
            </div>
          </div>
          <span className="font-mono text-[9px] tracking-[0.28em] text-white/50 whitespace-nowrap">
            NOTIFY →
          </span>
        </div>
        <div className="mt-3 flex items-center justify-between font-mono text-[9px] tracking-[0.3em] text-silver-300/80">
          <span>{item.cat}</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1 h-1 rounded-full bg-silver-100 animate-pulse" />
            SOON
          </span>
        </div>
      </div>

      {/* Hover sweep */}
      <div className="absolute inset-x-0 top-0 h-px bg-silver-100 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 z-40" />

      {/* Corner ticks */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 w-3 h-px bg-white/20" />
    </div>
  );
};

export const Supplements = () => {
  return (
    <section
      id="supplements"
      data-testid="section-supplements"
      className="relative bg-black text-white py-32 md:py-40 border-t border-white/5"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="grid grid-cols-12 gap-6 items-end mb-16 md:mb-20 border-b border-white/10 pb-8"
        >
          <div className="col-span-12 md:col-span-8">
            <div className="flex items-center gap-3 mb-3">
              <span className="font-mono text-[10px] tracking-[0.42em] text-silver-300">
                §06 — SUPPLEMENTS
              </span>
              <span className="inline-flex items-center gap-2 px-2.5 py-1 border border-silver-100/40 rounded-full font-mono text-[9px] tracking-[0.32em] uppercase text-silver-100">
                <span className="w-1.5 h-1.5 rounded-full bg-silver-100 animate-pulse" />
                Coming Soon
              </span>
              <span className="hidden md:inline-flex items-center gap-2 px-2.5 py-1 rounded-full font-mono text-[9px] tracking-[0.32em] uppercase text-white bg-white/[0.06]">
                Just Level Up · Line
              </span>
            </div>
            <h2 className="font-serif text-[10vw] md:text-[5vw] leading-[0.95] tracking-[-0.02em] text-white">
              Fuel &amp;
              <br />
              <span className="italic text-silver-100">the ritual bench.</span>
            </h2>
          </div>
          <p className="col-span-12 md:col-span-4 self-end text-white/55 text-[14px] leading-[1.75]">
            Supplements and vessels for the warrior between sets. Third-party
            tested, small batch, sealed by the numbered. Includes the new{" "}
            <span className="text-white">Just Level Up</span> line — pre-workout,
            protein, amino, and RTD energy.
          </p>
        </motion.div>

        {/* Asymmetric grid: 1 featured (hydration) + 5 items */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
          <motion.a
            href="#enlist"
            data-testid="supplement-item-01"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="md:row-span-2 md:col-span-1"
          >
            <SupplementTile item={SUPPLEMENTS[0]} feature />
          </motion.a>

          {SUPPLEMENTS.slice(1).map((item, i) => (
            <motion.a
              key={item.n}
              href="#enlist"
              data-testid={`supplement-item-${item.n}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{
                duration: 0.7,
                delay: 0.1 + (i % 2) * 0.08,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              <SupplementTile item={item} />
            </motion.a>
          ))}
        </div>

        {/* Footer strip */}
        <div className="mt-16 md:mt-20 border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4 text-white/50 font-mono text-[10px] tracking-[0.32em] uppercase">
          <div>06 SKUs · tested · numbered · dispatch-only</div>
          <a
            href="#enlist"
            className="text-silver-100 hover:text-white transition-colors flex items-center gap-3"
          >
            Join the apothecary list
            <span className="w-8 h-px bg-silver-100" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Supplements;
